import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { markAuthReady } from '../services/authReady';
import { getBackendUrl } from '../services/runtimeConfig';

const AuthContext = createContext(null);

const API_URL = getBackendUrl() + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
      markAuthReady();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, { _silent: true });
      setUser(response.data);
    } catch (error) {
      if (!error.response) {
        // Erreur réseau (démarrage à froid) — jusqu'à 3 tentatives silencieuses espacées
        const delays = [2000, 4000, 6000];
        for (const delay of delays) {
          try {
            await new Promise(r => setTimeout(r, delay));
            const retry = await axios.get(`${API_URL}/auth/me`, { _silent: true });
            setUser(retry.data);
            return;
          } catch (retryError) {
            if (retryError.response) {
              // Le serveur répond mais refuse (ex: 401) — on sort du retry
              break;
            }
          }
        }
        // Toutes les tentatives réseau ont échoué : on garde le token,
        // l'utilisateur peut recharger la page manuellement
        console.error('Serveur inaccessible après plusieurs tentatives');
      } else if (error.response.status === 401) {
        // Token invalide ou expiré — déconnexion légitime
        logout();
      } else {
        console.error('Erreur de récupération utilisateur:', error);
      }
    } finally {
      setLoading(false);
      markAuthReady();
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.put(`${API_URL}/auth/password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isGestionnaire = () => user?.role === 'gestionnaire' || isAdmin();
  const isLecteur = () => user?.role === 'lecteur';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      changePassword,
      isAdmin,
      isGestionnaire,
      isLecteur,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};
